import "intersection-observer"; // polyfill for old iPhones
import "lazysizes";
import "lazysizes/plugins/attrchange/ls.attrchange";
import React, { useCallback, useEffect, useState } from "react";
import { AppProps, NextWebVitalsMetric } from "next/app";
import { ApolloProvider } from "@apollo/client";
import { useRouter } from "next/router";
import Router from "next/router";

import Head from "next/head";
import { Analytics } from "@vercel/analytics/react";
import useAsyncEffect from "use-async-effect";
import moment, { Moment } from "moment-timezone";
import { SpeedInsights } from "@vercel/speed-insights/next";

import { userSetToken, userGetToken, userRefreshToken, userGetClaims, userDeleteToken } from "src/services/UserService";
import AnalyticsService from "src/services/AnalyticsService";
import Page from "src/components/Page";
import FallbackComponent from "src/components/FallbackComponent";
import { PortalProvider } from "src/components/Portal";
import { currentSport, isHeadless, resolveHeadlessMode, sportCountMismatch } from "src/services/ApolloService";
import { RENDERED_AT_PROP_NAME, useApollo } from "src/lib/apollo-client";
import { RenderedAtContext, setPageHydrated } from "src/services/DateService";
import { DEFAULT_SPORT, IS_SERVER, SPORTS_MAP } from "src/constants";
import { LEAGUE_PROMOS_QUERY } from "src/gql/queries";
import {
  LeaguePromo,
  SubscriptionContext,
  getActiveLeaguePromo,
  getProductTier,
  getSubscriptionFromToken,
  getTierSportCount,
} from "src/services/SubscriptionService";
import { getPreferredUserLocale } from "src/services/GeolocationService";
import { ErrorBoundary } from "react-error-boundary";
import { capturePostHogEvent, resetPostHog, setupPostHog } from "src/services/PostHogService";

export function reportWebVitals({ id, name, label, value }: NextWebVitalsMetric) {
  AnalyticsService.track(`Next.js ${label} metric`, {
    eventAction: name,
    eventValue: Math.round(name === "CLS" ? value * 1000 : value), // values must be integers
    eventLabel: id, // id unique to current page load
  });
}

// Track client-side page views
const handleRouteChange = () => {
  AnalyticsService.page(window.location.href);
  capturePostHogEvent("$pageview");
};

const syncHeadlessShell = (url: string) => {
  const targetUrl = new URL(url, window.location.origin);
  const queryHeadless = targetUrl.searchParams.get("headless");
  const headlessMode = resolveHeadlessMode(
    targetUrl.pathname,
    `${targetUrl.pathname}${targetUrl.search}`,
    queryHeadless ?? undefined
  );

  if (targetUrl.pathname === "/subscribefunnel") {
    document.documentElement.dataset.headlessShell = "funnel";
    isHeadless(headlessMode);
    return;
  }

  if (headlessMode) {
    document.documentElement.dataset.headlessShell = "true";
    try {
      window.sessionStorage.setItem("headlessMode", headlessMode);
    } catch (e) {}
    isHeadless(headlessMode);
    return;
  }

  if (queryHeadless === "0") {
    delete document.documentElement.dataset.headlessShell;
    try {
      window.sessionStorage.removeItem("headlessMode");
    } catch (e) {}
    isHeadless("");
    return;
  }

  let storedHeadlessMode = "";
  try {
    storedHeadlessMode = window.sessionStorage.getItem("headlessMode") ?? "";
  } catch (e) {
    storedHeadlessMode = "";
  }
  if (storedHeadlessMode) {
    document.documentElement.dataset.headlessShell = "true";
  } else {
    delete document.documentElement.dataset.headlessShell;
  }
  isHeadless(storedHeadlessMode);
};

// Detect change and update currentSport
const handleRouteChangeStart = (url: string) => {
  syncHeadlessShell(url);
  const parts = url.split("/");
  currentSport(SPORTS_MAP[parts[1]]);
};

const MyApp = ({ Component, pageProps }: AppProps) => {
  const router = useRouter();
  const apolloClient = useApollo(pageProps);

  // On the server, reactive vars are shared by all requests. Set the sport from this
  // page's route, the same way the client initializes it, so both render the same nav.
  if (IS_SERVER) {
    currentSport(SPORTS_MAP[router.query.sport as string] ?? DEFAULT_SPORT);
  }

  useEffect(setPageHydrated, []);

  useEffect(() => {
    const token = process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN;
    if (!token) {
      if (process.env.NODE_ENV !== "production") {
        console.error(
          "NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN is configured"
        );
      }
      return;
    }
    return setupPostHog(token);
  }, []);

  const [init, setInit] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [userLocale, setUserLocale] = useState(getPreferredUserLocale());
  const [loginModalVisible, setLoginModalVisible] = useState(false);
  const [pricingModalVisible, setPricingModalVisible] = useState(false);
  const [loginTabIndex, setLoginTabIndex] = useState(0);
  // The cookie refresh will happen server side, so we can immediately
  // check the token for the latest subscription value when we load the
  // app.
  const [subscriptionLevel, setSubscriptionLevel] = useState<string | null>(null);
  const [subscriptionSports, setSubscriptionSports] = useState<string[]>([]);
  const [subscriptionSportsUpdateTime, setSubscriptionSportsUpdateTime] = useState<Moment>();
  const [subscriptionUpgradeSource, setSubscriptionUpgradeSource] = useState<string | null>(null);
  const [subscriptionDefaultTierIndex, setSubscriptionDefaultTierIndex] = useState<number | null>(null);
  const [subscriptionDefaultSports, setSubscriptionDefaultSports] = useState<string[] | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null);
  const [leaguePromos, setLeaguePromos] = useState<LeaguePromo[]>([]);

  // Keep headless mode synchronized with the active URL, including clearing it when it is explicitly disabled.
  useEffect(() => {
    syncHeadlessShell(router.asPath);
  }, [router.asPath]);

  // League promos unlock leagues without a subscription, so they are not in the token.
  // Load them after each store reset, which would cancel a query in flight.
  const loadLeaguePromos = () =>
    apolloClient
      .query({ query: LEAGUE_PROMOS_QUERY, fetchPolicy: "network-only" })
      .then(({ data }) => setLeaguePromos(data?.me?.leaguePromos ?? []))
      .catch(console.error);

  const initUserData = useCallback((token: string) => {
    // Initialize user state
    userSetToken(token);
    setSignedIn(true);
    const { sub: userId, email, locale, sports, sut, src } = userGetClaims(token);
    const subLvl = getSubscriptionFromToken(token);
    setSubscriptionLevel(subLvl);
    setSubscriptionSports(sports);
    setUserLocale(getPreferredUserLocale(locale));
    if (sut) {
      setSubscriptionSportsUpdateTime(moment.utc(sut).local());
    }
    setPaymentMethod(src);

    // Detect sport mismatch
    const properSportCount = getTierSportCount(getProductTier(subLvl));
    if (properSportCount && properSportCount !== sports.length) {
      sportCountMismatch(true);
    }
    apolloClient.resetStore().catch(console.error).then(loadLeaguePromos);
    AnalyticsService.identify(userId, {
      email,
    });
  }, []);

  useEffect(() => {
    // Set the token from URL query param is provided
    if (typeof router.query.token === "string") {
      const url = new URL(router.asPath, window.location.origin);
      url.searchParams.delete("token");
      router.replace(url.pathname, undefined, { shallow: true });
      initUserData(router.query.token);
    }
  }, [router.query.token]);

  // Initialize user specific data
  useAsyncEffect(async () => {
    // Refresh the token
    let token = await userRefreshToken(userGetToken());
    if (token) {
      initUserData(token);
    }
    router.events.on("routeChangeComplete", handleRouteChange);
    router.events.on("routeChangeStart", handleRouteChangeStart);
    // Let SubscriptionContext consumers know that we've initialized
    setInit(true);
    return () => {
      router.events.off("routeChangeStart", handleRouteChangeStart);
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, []);

  /**
   * a function that gets called when we close the login modal in the wrapper
   * This sets the user token through the user service and rerenders the state
   * @param userInfo {object} The user information that is parsed from the login call
   * @param isLogin {boolean} Whether or not this is a sign up or login
   */
  const onSignIn = async (
    userInfo?: any,
    isLogin?: boolean,
    source?: string,
    page?: string,
    options?: { skipRouterPush?: boolean }
  ) => {
    if (userInfo?.token) {
      const { token } = userInfo;
      userSetToken(token);
      const { sub: userId, email, locale, sports, sut: sportsUpdateTime, src } = userGetClaims(token);

      // It is important to do reset store here because reset store will refire all queries and we want all queries to be refired now that they have logged in.
      await apolloClient.resetStore();
      loadLeaguePromos();
      if (!options?.skipRouterPush) {
        // force getInitialProps to run again on the current page
        router.push(router.asPath);
      }

      setSignedIn(true);
      // Set subscription values to the users subscription
      setSubscriptionLevel(getSubscriptionFromToken(token));
      setSubscriptionSports(sports);
      setSubscriptionSportsUpdateTime(sportsUpdateTime ? moment(sportsUpdateTime) : undefined);
      setUserLocale(getPreferredUserLocale(locale));
      setLoginModalVisible(false);
      setLoginTabIndex(0);
      setPaymentMethod(src);

      if (isLogin) {
        AnalyticsService.identify(userId, {
          email,
        });
        AnalyticsService.track("Login", {
          source,
        });
      } else {
        AnalyticsService.identify(userId, {
          email,
          page,
        });
        AnalyticsService.track("Signup", {
          email,
          source,
          page,
          state: userLocale,
          external_id: userId,
        });
      }
    } else {
      setLoginModalVisible(false);
      setLoginTabIndex(0);
    }
  };

  /**
   * Set the users state locally to be used throughout the app
   */
  const setLocale = (token: string) => {
    const { locale } = userGetClaims(token);
    setUserLocale(getPreferredUserLocale(locale));
  };

  /**
   * This function sets the subscription properties we need to determine
   * which features to gate and when they can update their sports
   */
  const setUserSubscriptionLevel = async (token: string) => {
    // Set token
    userSetToken(token);

    const claims = userGetClaims(token);

    // Update app state
    setSubscriptionLevel(getSubscriptionFromToken(token));
    setSubscriptionSports(claims.sports);
    setSubscriptionSportsUpdateTime(claims.sut ? moment(claims.sut) : undefined);
    setPaymentMethod(claims.src);

    // Refetch everything
    await apolloClient.resetStore();

    // force getInitialProps to run again on the current page
    router.push(router.asPath);
  };

  /**
   * Determine if the users subscription level gives them access to a feature
   * @param sportKey {string} The sport key to compare against the users list of approved sports
   * @param isSharpStat {boolean} The users email
   */
  const hasSubscriptionAccess = (sportKey: string, isSharpStat?: boolean) => {
    // A league promo unlocks everything in its league, like a free game does
    if (signedIn && getActiveLeaguePromo(leaguePromos, sportKey)) {
      return true;
    }

    // Any and all subscribers get access to WC content
    if (signedIn && subscriptionLevel && sportKey === "WC") {
      return true;
    }

    if (
      signedIn &&
      subscriptionLevel &&
      (subscriptionLevel.includes("sharp") || // if the subscription is sharp, they get it all
        (!isSharpStat && // if its a sharp stat and they aren't sharp, goodbye
          (subscriptionLevel.includes("vip") || // if it's not a sharp stat and they are vip, pro or prem lets check sports
            subscriptionLevel.includes("pro") ||
            subscriptionLevel.includes("prem")) &&
          subscriptionSports?.includes(sportKey)))
    ) {
      return true;
    }
    return false;
  };

  /**
   * This function will redirect the user to the pricing page
   * @deprecated
   * @param trackingProperties properties to attach to the tracking event
   * @param subscriptionUpgradeSource
   */
  const redirectToPricing = (track: any, source: string, defaultTierIndex?: number, defaultSports?: string[]) => {
    if (paymentMethod === "PAYPAL") {
      Router.push("/account").then(() => window.scrollTo(0, 0));
      return;
    }

    togglePricingModal({
      track,
      source,
      defaultTierIndex,
      defaultSports,
    });
  };

  /**
   * For non-subscribers (anonymous or free), route to /subscribe. Subscribers
   * still get the in-place upgrade/downgrade modal.
   */
  const togglePricingModal = ({
    track,
    source = null,
    defaultTierIndex = null,
    defaultSports = null,
  }: {
    track?: any;
    source?: string | null;
    defaultTierIndex?: number | null;
    defaultSports?: string[] | null;
  } = {}) => {
    if (paymentMethod === "PAYPAL") {
      Router.push("/account").then(() => window.scrollTo(0, 0));
      return;
    }

    AnalyticsService.track("Click Upgrade Modal", {
      page: window.location.href,
      sport: currentSport()?.name,
      source,
      ...track,
    });

    if (!subscriptionLevel) {
      const sportKey = currentSport()?.key;
      const query: { source?: string; sport?: string } = {};
      if (source) {
        query.source = source;
      }
      if (sportKey) {
        query.sport = sportKey;
      }
      Router.push({ pathname: "/subscribe", query });
      return;
    }

    setPricingModalVisible(!pricingModalVisible);
    setSubscriptionUpgradeSource(source);
    setSubscriptionDefaultTierIndex(defaultTierIndex);
    setSubscriptionDefaultSports(defaultSports);
  };

  /**
   * Set which login modal view we show while the modal is open
   */
  const setTabIndex = (index: number) => setLoginTabIndex(index);

  const openLoginModal = () => {
    setLoginModalVisible(true);
    setLoginTabIndex(0);
  };

  /**
   * Log the user out
   * This removes the token from local storage
   * Then it clears the client store
   * Finally it calls set state and wipes the current state
   */
  const logoutUser = async () => {
    userDeleteToken();
    // It is important to do clear store here because reset store will refire all queries and the rerender from setState will also refire queries so they will conflict and throw errors.  Clear store is identical to reset store but queries are not refired.
    setSignedIn(false);
    setSubscriptionLevel(null);
    setSubscriptionSports([]);
    setSubscriptionSportsUpdateTime(undefined);
    setPaymentMethod(null);
    setLeaguePromos([]);
    apolloClient.resetStore();
    resetPostHog();
    // force getInitialProps to run again on the current page
    router.push(router.asPath);
    window.localStorage.clear();
  };

  return (
    <ErrorBoundary fallbackRender={FallbackComponent}>
      <ApolloProvider client={apolloClient}>
        <SubscriptionContext.Provider
          value={{
            init,
            signedIn,
            onSignIn,
            userLocale,
            setLocale,
            setSubscriptionLevel: setUserSubscriptionLevel,
            hasSubscriptionAccess,
            subscriptionLevel,
            subscriptionSports,
            leaguePromos,
            subscriptionSportsUpdateTime,
            subscriptionUpgradeSource,
            paymentMethod,
            subscriptionDefaultTierIndex,
            subscriptionDefaultSports,
            pricingModalVisible,
            redirectToPricing,
            togglePricingModal,
            loginModalVisible,
            loginTabIndex,
            setLoginTabIndex: setTabIndex,
            openLoginModal,
            logoutUser,
          }}
        >
          <RenderedAtContext.Provider value={pageProps[RENDERED_AT_PROP_NAME]}>
            <PortalProvider>
              <Page {...pageProps}>
                <Head>
                  <meta charSet="utf-8" />
                  <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no" />
                  <meta name="theme-color" content="#00214d" />
                  <meta name="format-detection" content="telephone=no" />
                </Head>
                <Component {...pageProps} />
                <Analytics />
                <SpeedInsights sampleRate={0.1} />
              </Page>
            </PortalProvider>
          </RenderedAtContext.Provider>
        </SubscriptionContext.Provider>
      </ApolloProvider>
    </ErrorBoundary>
  );
};

export default MyApp;
